import React from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import WelcomePiano from '../../components/admin/WelcomePiano';
import { MusicIcon } from '../../components/shared';

const AdminPanel: React.FC = () => {
  const { releases, earnings, artists, isLoading } = useData();
  const { user } = useAuth();
  const { t } = useTranslation();

  const pendingReleases = releases.filter(r => r.status === 'pending' || r.status === 'submitted');
  const publishedReleases = releases.filter(r => r.status === 'published' || r.status === 'approved');
  const rejectedReleases = releases.filter(r => r.status === 'rejected');
  const totalEarnings = earnings.reduce((total, earning) => total + parseFloat(earning.revenue), 0);
  const activeArtists = artists.filter(a => a.status === 'active');

  const totalMusic = releases.length;

  // Extract admin name from email
  const adminName = user?.email?.split('@')[0] || 'Admin';
  const capitalizedName = adminName.charAt(0).toUpperCase() + adminName.slice(1);

  const adminStats = [
    {
      title: 'Toplam Müzik',
      value: totalMusic.toString(),
      href: '/admin/releases',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
        </svg>
      ),
      change: rejectedReleases.length > 0 ? `${rejectedReleases.length} red` : 'Hepsi aktif',
      changeType: rejectedReleases.length > 0 ? 'decrease' as const : 'neutral' as const,
      gradient: 'from-blue-500 to-purple-600',
      showRejectedCount: true
    },
    {
      title: t('admin.pendingApprovalStats'),
      value: pendingReleases.length.toString(),
      href: '/admin/pending',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      change: pendingReleases.length > 0 ? `${pendingReleases.length} bekliyor` : 'Hepsi onaylandı',
      changeType: pendingReleases.length > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Onaylanmış Müzikler',
      value: publishedReleases.length.toString(),
      href: '/admin/releases',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      change: `${publishedReleases.length}`,
      changeType: publishedReleases.length > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      title: t('admin.activeArtistStats'),
      value: activeArtists.length.toString(),
      href: '/admin/artists',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      change: activeArtists.length > 0 ? `${activeArtists.length} aktif` : '0',
      changeType: activeArtists.length > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-indigo-500 to-blue-600'
    },
    {
      title: t('admin.totalRevenueStats'),
      value: `₺${totalEarnings.toFixed(2)}`,
      href: '/admin/earnings',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      change: totalEarnings > 0 ? `₺${totalEarnings.toFixed(2)}` : '₺0.00',
      changeType: totalEarnings > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-purple-500 to-pink-600'
    }
  ];

  const quickActions = [
    {
      title: t('admin.addNewArtistAction'),
      description: t('admin.addNewArtistDesc'),
      href: '/admin/artists',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      )
    },
    {
      title: t('admin.publishSongAction'),
      description: t('admin.publishSongDesc'),
      href: '/admin/releases/new',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      )
    },
    {
      title: t('admin.pendingApprovalsAction'),
      description: t('admin.pendingApprovalsDesc'),
      href: '/admin/pending',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: t('admin.revenueReportsAction'),
      description: t('admin.revenueReportsDesc'),
      href: '/admin/earnings',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Welcome Piano Component */}
      <WelcomePiano adminName={capitalizedName} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {isLoading ? (
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl"></div>
                    <div className="w-16 h-6 bg-white/20 rounded-full"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-white/20 rounded w-2/3"></div>
                    <div className="h-8 bg-white/20 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          adminStats.map((stat) => (
            <Link key={stat.title} to={stat.href} className="group">
              <div className="relative overflow-hidden bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} text-white shadow-lg`}>
                    {stat.icon}
                  </div>
                  <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                    stat.changeType === 'increase'
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : stat.changeType === 'decrease'
                      ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                      : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                    {stat.change}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-white/70 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-xl mb-8">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">{t('admin.quickActionsTitle')}</h3>
          <p className="text-sm text-white/70">{t('admin.quickActionsDesc')}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="group flex items-start p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105"
              >
                <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-orange-500 to-purple-600 text-white shadow-lg group-hover:shadow-xl transition-shadow">
                  {action.icon}
                </div>
                <div className="ml-4">
                  <h4 className="text-sm font-medium text-white group-hover:text-orange-300 transition-colors">
                    {action.title}
                  </h4>
                  <p className="text-sm text-white/60 mt-1">
                    {action.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">{t('admin.recentActivityTitle')}</h3>
        </div>
        <div className="p-6">
          {pendingReleases.length > 0 ? (
            <div className="space-y-4">
              <h4 className="font-medium text-white flex items-center">
                <svg className="w-5 h-5 mr-2 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('admin.pendingApprovals')}:
              </h4>
              {pendingReleases.slice(0, 3).map((release) => (
                <div key={release.id} className="flex items-center justify-between py-3 px-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-200">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <MusicIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{release.title}</p>
                      <p className="text-sm text-white/60">{release.artist_display_name}</p>
                    </div>
                  </div>
                  <Link
                    to="/admin/pending"
                    className="text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors flex items-center"
                  >
                    {t('admin.review')}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-white/70">
                {t('admin.noPendingCurrentActivity')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 